/**
 * Component for the message input area in the tutor conversation
 * Styled to match the MessageInput component
 */
import Button, { ButtonSize } from "@shared/components/ui/Button";
import theme from "@shared/utils/themeColors";
import React, { KeyboardEvent, useEffect, useState } from "react";

interface TutorMessageInputProps {
  onSendMessage: (message: string) => void;
  disabled?: boolean;
}

const TutorMessageInput = React.forwardRef<HTMLTextAreaElement, TutorMessageInputProps>((
  { onSendMessage, disabled = false },
  ref
) => {
  const [message, setMessage] = useState("");

  const handleSend = () => {
    if (message.trim() && !disabled) {
      onSendMessage(message.trim());
      setMessage("");
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSend();
  };

  // Auto-resize textarea based on content
  const autoResizeTextarea = (element: HTMLTextAreaElement) => {
    if (!element) return;

    // Reset height to calculate the right height
    element.style.height = "auto";

    // Set new height (scrollHeight + border width)
    const newHeight = Math.min(element.scrollHeight, 150); // Max height 150px
    element.style.height = `${newHeight}px`;
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    autoResizeTextarea(e.target);
  };

  // Initialize textarea height
  useEffect(() => {
    const textarea = document.querySelector("textarea");
    if (textarea) {
      autoResizeTextarea(textarea as HTMLTextAreaElement);
    }
  }, []);

  return (
    <div className={`border-t ${theme.border.primary} p-4`}>
      <form className="flex items-center gap-2" onSubmit={handleSubmit}>
        <div className="grow mr-3 relative">
          <textarea
            ref={ref}
            value={message}
            onChange={handleTextareaChange}
            onKeyDown={handleKeyDown}
            placeholder="Ask a Tutor..."
            className={`form-input w-full ${theme.bg.primary} focus:bg-white dark:focus:bg-gray-800 placeholder-gray-500 min-h-[60px] mt-1 resize-none p-3 rounded-lg`}
            disabled={disabled}
          />
        </div>
        <Button
          type="submit"
          disabled={!message.trim() || disabled}
          size={ButtonSize.MEDIUM}
        >
          Send
        </Button>
      </form>
      <p className={`text-xs mt-2 ${theme.text.muted}`}>
        Press Enter to send, Shift+Enter for a new line
      </p>
    </div>
  );
});

export default TutorMessageInput;
